// Sprint P0.1 · Sidebar V3 mutualisée — iso-fonctionnelle et iso-visuelle.
// Une seule définition de la navigation, une seule logique de badge.
// Consommée par les 6 écrans agence V3 (Pilotage, Planning, Documents,
// Fiche Dossier, Settings — Espace Client a son propre layout mobile).

import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, CalendarClock, MessageCircle,
  FileText, Settings, HelpCircle, ChevronDown, ArrowRight, FileSignature,
} from "lucide-react";
import { LogoGlyph, LogoWordmark } from "@/branding/Logo";
import { useUnread } from "@/context/UnreadContext";

const NAV = [
  { key: "dashboard",  to: "/app/dashboard",   label: "Pilotage",  icon: LayoutDashboard, end: true },
  { key: "dossiers",   to: "/app/dossiers",    label: "Dossiers",  icon: FolderKanban },
  { key: "mandats",    to: "/app/mandats",     label: "Mandats",   icon: FileSignature },
  { key: "planning",   to: "/app/planning",    label: "Planning",  icon: CalendarClock },
  { key: "messages",   to: "/app/dossiers",    label: "Messages",  icon: MessageCircle, badgeKey: "conversationsWithUnread" },
  { key: "documents",  to: "/app/documents",   label: "Documents", icon: FileText },
  { key: "parametres", to: "/app/parametres",  label: "Réglages",  icon: Settings },
];

export const AppSidebar = ({ agency, user, testId = "app-sidebar" }) => {
  const unread = useUnread();
  const initials = (agency?.name || "W Y")
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <aside
      data-testid={testId}
      className="fixed left-0 top-0 z-30 hidden h-screen w-[260px] flex-col lg:flex"
      style={{ background: "linear-gradient(180deg, #080808 0%, #0C0C0C 50%, #131313 100%)" }}
    >
      {/* Logo — asset officiel wyvio */}
      <div className="flex items-center gap-2.5 px-7 pt-9 pb-14" data-testid={`${testId}-logo`}>
        <LogoGlyph size={28} />
        <LogoWordmark size={22} color="#FFFFFF" />
      </div>

      {/* Nav */}
      <nav className="px-5">
        <ul className="flex flex-col gap-0.5">
          {NAV.map(({ key, to, label, icon: Icon, end, badgeKey }) => {
            const badge = badgeKey ? unread?.[badgeKey] : 0;
            return (
              <li key={key}>
                <NavLink
                  to={to}
                  end={end}
                  data-testid={`nav-${key}`}
                  className={({ isActive }) => {
                    const base = "group relative flex items-center gap-3 rounded-xl px-3.5 py-3 text-[13.5px] font-medium transition-colors";
                    return isActive
                      ? `${base} bg-white/[0.06] text-white`
                      : `${base} text-white/60 hover:text-white hover:bg-white/[0.03]`;
                  }}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute left-[-2px] top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[#C8FF2F] shadow-[0_0_10px_rgba(200,255,47,0.7)]" />
                      )}
                      <Icon size={17} strokeWidth={1.75} />
                      <span className="flex-1">{label}</span>
                      {badge > 0 && (
                        <span
                          data-testid={`nav-${key}-badge`}
                          className="rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold text-black"
                          style={{ background: "#C8FF2F" }}
                        >
                          {badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Support */}
      <div className="mt-auto p-5">
        <div
          className="rounded-2xl p-5"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%)",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
          }}
        >
          <div className="flex items-start gap-2.5 text-white">
            <HelpCircle size={16} strokeWidth={1.6} className="mt-[2px] opacity-55" />
            <div className="text-[13px] font-medium leading-snug">
              Une question<br />sur un dossier ?
            </div>
          </div>
          <p className="mt-3 text-[11.5px] leading-relaxed text-white/45">
            Notre équipe répond<br />en moins de 2h.
          </p>
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#C8FF2F] transition-opacity hover:opacity-80"
            data-testid={`${testId}-support-btn`}
          >
            Contacter le support <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* User */}
      <div className="border-t border-white/[0.06] px-6 py-4" data-testid={`${testId}-user`}>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[11px] font-semibold text-white ring-1 ring-white/10">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] font-medium text-white">
              {agency?.name || "wyvio"}
            </div>
            <div className="truncate text-[11px] text-white/45">{user?.name || ""}</div>
          </div>
          <ChevronDown size={14} className="text-white/40" />
        </div>
      </div>
    </aside>
  );
};

export default AppSidebar;
